import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Popup, Button, Tooltip } from 'next-common-lib';
import { useFormik } from 'formik';
import i18n from 'i18n';
import TextFieldFormik from 'components/Common/InputBox';
import TblList from 'components/Common/Template/TblList';
import PopConfirmButton from 'components/Common/Buttons/PopConfirmButton';
import ProgressPopup from 'components/Common/Popup/ProgressPopup';
import * as popupActions from 'redux/ducks/common/popupDucks';
import * as wizardActions from 'redux/ducks/common/wizardDuck';
import * as gnbMenuActions from 'redux/ducks/GnbMenu/GnbMenuDucks';
import { GnbGroupMenuSchema } from './GnbMenuSchema';

export const PopupGnbMenuGroup = ({ pageList, removePageList, setPageList }) => {
  const dispatch = useDispatch();
  const isPopup = useSelector(state => state.wizard.isPopup);
  const adminVo = useSelector(state => state.adminInfo.adminVo);
  const menu = useSelector(state => state.menu);
  const isShowProgressPopup = useSelector(state => state.popup.ProgressPopup.isShow);

  const formik = useFormik({
    initialValues: { titleKo: '', titleEn: '', pageList: [] },
    validationSchema: GnbGroupMenuSchema,
    onSubmit: values => {
      const payload = {
        titleKo: values.titleKo,
        titleEn: values.titleEn,
        currentCompId: adminVo.compCd,
        menuType: 'CONTAINER',
        urlType: 'O',
        fullScreenYn: 'N',
        pageLists: pageList,
        activeYn: 'Y',
        servicedLanguages: ['ko', 'en', 'ja', 'zh'],
        servicedNetworks: ['EX', 'IN'],
        servicedLocales: ['KR', 'GB', 'US'],
        servicedBrowsers: ['IE', 'ETC'],
      };
      dispatch(gnbMenuActions.createGnbMenu(payload));
      dispatch(popupActions.openProgressPopup());
    },
  });

  useEffect(() => {
    if (menu.isSuccess) {
      dispatch(wizardActions.closePopup());
      dispatch(popupActions.closeProgressPopup());
      setPageList([]);
      formik.handleReset();
    }
  }, [menu.isSuccess]);

  useEffect(() => {
    formik.setFieldValue('pageList', pageList);
  }, [pageList]);

  const handleOpenGnbPopup = name => {
    dispatch(wizardActions.openGnbPopup({ name }));
    dispatch(wizardActions.closePopup());
    dispatch(popupActions.closeProgressPopup());
  };

  const handleOnClose = () => {
    formik.handleReset();
    setPageList([]);
    dispatch(wizardActions.closePopup());
    dispatch(popupActions.closeProgressPopup());
  };

  return (
    <>
      {isPopup && (
        <Popup
          id="GnbMenuPopup"
          isShow={isPopup}
          isDimed={isPopup}
          dimedClassName="transparent"
          isDimedOverlap
          zIndex={2500}
          size="sm w-popup"
          componentType="normalPopup"
          closeFunction={() => {
            handleOnClose();
          }}
          headerComponent={[
            <h1 className="popup-title" key={1}>
              {i18n.t('BTY_W_T_CREATE_MENU_GROUP')}
            </h1>,
          ]}
          contentsComponent={[
            <div className="article-body">
              <TblList>
                <div className="cell" role="rowheader">
                  <div className="inner-row">
                    <strong className="title">{i18n.t('BTY_W_T_TITLE_KO')}</strong>
                  </div>
                </div>
                <div className="cell">
                  <TextFieldFormik
                    id="titleKo"
                    formik={formik}
                    placeholderLabel={i18n.t('BTY_W_T_TITLE_KO_INFO')}
                    label={formik.getFieldProps('titleKo').value}
                    maxLength={30}
                    useExpLabel={formik.errors.titleKo !== undefined}
                    autoFocus
                  />
                </div>
              </TblList>
              <TblList>
                <div className="cell" role="rowheader">
                  <div className="inner-row">
                    <strong className="title">{i18n.t('BTY_W_T_TITLE_EN')}</strong>
                  </div>
                </div>
                <div className="cell">
                  <TextFieldFormik
                    id="titleEn"
                    formik={formik}
                    label={formik.getFieldProps('titleEn').value}
                    placeholderLabel={i18n.t('BTY_W_T_TITLE_EN_INFO')}
                    maxLength={30}
                    useExpLabel={formik.errors.titleEn !== undefined}
                  />
                </div>
              </TblList>

              <TblList>
                <div className="inner-row">
                  <strong className="title">{i18n.t('BTY_W_T_LOW_MENU')}</strong>
                </div>
              </TblList>
              <div className="sub-menu">
                <div className="sub-menu-list">
                  {[...Array(3)].map((n, index) => {
                    return (
                      <div key={index}>
                        {pageList[index] ? (
                          <div className="view-box-content-group">
                            <Tooltip componentType="move" label={[`${pageList[index].titleKo}`]} placement="bottom">
                              <span className="menu-title">{pageList[index].titleKo}</span>
                            </Tooltip>
                            <Button
                              className="sm-close"
                              size="xxs"
                              title={i18n.t('EM_T_DELETE')}
                              iconInfo={{ className: 'ic-close', size: 'sm' }}
                              onClick={() => {
                                removePageList(pageList[index].count); // id 값으로 변경
                              }}
                            />
                          </div>
                        ) : (
                          <Button
                            className="text-title sub-main-text"
                            componentType="noneBtn"
                            label={i18n.t('BTY_W_T_LOW_MENU_ADD')}
                            title={i18n.t('BTY_W_T_LOW_MENU_ADD')}
                            onClick={() => handleOpenGnbPopup('GNBPAGELIST')}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {formik.touched.pageList && !!formik.getFieldMeta('pageList').error && (
                <div className="input-add-exp error">
                  <span className="text color-warning">{formik.errors.pageList}</span>
                </div>
              )}
            </div>,
          ]}
          bottomComponent={[<PopConfirmButton confirmAction={formik.submitForm} cancelAction={handleOnClose} />]}
        />
      )}
      {isShowProgressPopup && <ProgressPopup />}
    </>
  );
};
PopupGnbMenuGroup.propTypes = {
  setPageList: PropTypes.func,
  pageList: PropTypes.arrayOf(PropTypes.object).isRequired,
  removePageList: PropTypes.func,
};
PopupGnbMenuGroup.defaultProps = { removePageList: () => {}, setPageList: () => {} };
export default PopupGnbMenuGroup;
